import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap } from 'rxjs/operators';
import { differenceInSeconds } from 'date-fns';
import { HttpContext, HttpParams } from '@angular/common/http';
import { environment } from '@env';
import { AddNoteParams, Note, RequestNoteParams, UpdateNoteParams, NotesRaw, NoteRaw } from '@shared/models';
import { AttachmentService, BackendService } from '@shared/services';
import { CUSTOM_ERROR_MESSAGE } from '@shared/interceptors';
import { ErrorUtils } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class NoteService {
  constructor(
    private backendService: BackendService,
    private attachmentService: AttachmentService,
  ) {}

  public addNote(noteData: AddNoteParams, customErrorNotification?: string): Observable<boolean> {
    return this.backendService
      .post<NoteRaw>(
        `${environment.noteService}`,
        { ...noteData },
        { context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, customErrorNotification) },
      )
      .pipe(
        exhaustMap((notesResponse) => {
          if (!noteData.attachments) {
            return of(notesResponse);
          }
          return from(noteData.attachments).pipe(
            mergeMap((attachment) =>
              from(
                this.attachmentService.createAttachment({
                  businessAccountId: notesResponse.businessAccountId,
                  entityId: notesResponse.id,
                  entityType: 'NOTE',
                  file: attachment.file!,
                  extension: attachment.extension!,
                  name: attachment.name,
                  description: attachment.description,
                }),
              ),
            ),
            catchError((error) => {
              ErrorUtils.catchError('attachmentService.createAttachment', error);
              return of(notesResponse);
            }),
            map(() => notesResponse),
          );
        }),
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public updateNote(noteId: Note['id'], noteData: UpdateNoteParams): Observable<boolean> {
    return this.backendService.patch<NoteRaw>(`${environment.noteService}/${noteId}`, noteData).pipe(
      exhaustMap((notesResponse) => {
        if (!noteData.attachments) {
          return of(notesResponse);
        }
        return from(noteData.attachments).pipe(
          mergeMap((attachment) =>
            from(
              this.attachmentService.createAttachment({
                businessAccountId: notesResponse.businessAccountId,
                entityId: noteId,
                entityType: 'NOTE',
                file: attachment.file!,
                extension: attachment.extension!,
              }),
            ),
          ),
          catchError((error) => {
            ErrorUtils.catchError('attachmentService.createAttachment', error);
            return of(notesResponse);
          }),
          map(() => notesResponse),
        );
      }),
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public deleteNote(noteId: Note['id']): Observable<boolean> {
    return this.backendService.delete<void>(`${environment.noteService}/${noteId}`).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getNotes({ entityType, entityId, page, size }: RequestNoteParams): Observable<Note[]> {
    let params = new HttpParams();

    if (page) {
      params = params.append('page', page);
    }

    if (size) {
      params = params.append('size', size);
    }

    return this.backendService
      .get<NotesRaw>(`${environment.noteService}?entityType=${entityType}&entityId=${entityId}&embed=attachments`, { params })
      .pipe(
        map((notesResponse) => this.mapNotes(notesResponse)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  private mapNotes(response?: NotesRaw): Note[] {
    const { content = [] } = response ?? {};
    return content.map((note) => ({
      ...note,
      shortText: note.contentText?.length > 500 ? note.contentText.slice(0, 495) + '...' : undefined,
      isUpdated: differenceInSeconds(new Date(note.updatedAt), new Date(note.createdAt)) > 0,
      attachments:
        note._embedded?.attachments?.map((item) => {
          return {
            ...item,
            downloadBtn: 'DOWNLOAD',
            rowClassName: `attachment-extension attachment-extension-${item.extension.toLowerCase()}`,
          };
        }) ?? [],
    }));
  }
}
